import React, { memo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import type { TrialResearchPreview } from '../../types';
import {
  applyAccentColor,
  applyPrimaryColor,
  HEX_COLOR,
  isTrialLogoSourceWithinLimit,
  prepareTrialLogo,
  resolveTrialLogoSources,
} from '../../utils/trial-preview';
import { BrandLogo } from './brand-logo';
import { TrialArrow, TrialBackArrow } from './trial-arrow';
import type { PreviewValues } from './preview-step';

interface HexColorFieldProps {
  id: string;
  label: string;
  hex: string;
  fallback: string;
  onHexChange: (hex: string) => void;
  onCommit: (hex: string) => void;
}

function HexColorField({
  id, label, hex, fallback, onHexChange, onCommit,
}: HexColorFieldProps) {
  const valid = HEX_COLOR.test(hex.trim());
  const pickerValue = (valid ? hex.trim() : fallback).toLowerCase();

  return (
    <div className="FreeTrial__Field">
      <label htmlFor={id}>{label}</label>
      <div className="FreeTrial__HexField">
        <label className="FreeTrial__HexSwatch">
          <input
            type="color"
            value={pickerValue}
            onChange={(event) => {
              const next = event.target.value.toLowerCase();

              onHexChange(next);
              onCommit(next);
            }}
            aria-label={label}
          />
        </label>
        <TextInput
          id={id}
          value={hex}
          onChange={(event) => onHexChange(event.target.value)}
          onBlur={() => {
            if (valid) onCommit(hex.trim());
          }}
          error={!valid}
        />
      </div>
    </div>
  );
}

type Props = {
  preview: TrialResearchPreview;
  values: PreviewValues;
  onChange: (values: PreviewValues) => void;
  onContinue: () => void;
  onBack: () => void;
};

export const BrandStep = memo(({
  preview, values, onChange, onContinue, onBack,
}: Props) => {
  const { t } = useTranslation();
  const [mainHex, setMainHex] = useState(values.colors.main);
  const [secondHex, setSecondHex] = useState(values.colors.button);
  const [logoError, setLogoError] = useState(false);
  const mainValid = HEX_COLOR.test(mainHex.trim());
  const secondValid = HEX_COLOR.test(secondHex.trim());
  const commitMain = useCallback((hex: string) => {
    setMainHex(hex);
    onChange({ ...values, colors: applyPrimaryColor(values.colors, hex) });
  }, [onChange, values]);
  const commitSecond = useCallback((hex: string) => {
    setSecondHex(hex);
    onChange({ ...values, colors: applyAccentColor(values.colors, hex) });
  }, [onChange, values]);
  const logoSources = resolveTrialLogoSources(preview, values);

  return (
    <form
      className={
        'FreeTrial__Glass max-md:!tw-mb-0 max-md:!tw-w-full max-md:!tw-max-h-[calc(100dvh-96px)] '
        + 'max-md:!tw-overflow-y-auto max-md:!tw-rounded-b-none max-md:!tw-rounded-t-[28px] max-md:!tw-border-0'
      }
      onSubmit={(e) => {
        e.preventDefault();
        if (!mainValid || !secondValid) return;

        let next = values;
        const main = mainHex.trim();
        const button = secondHex.trim();

        if (main.toLowerCase() !== values.colors.main.toLowerCase()) {
          next = { ...next, colors: applyPrimaryColor(next.colors, main) };
        }

        if (button.toLowerCase() !== next.colors.button.toLowerCase()) {
          next = { ...next, colors: applyAccentColor(next.colors, button) };
        }

        if (next !== values) onChange(next);
        onContinue();
      }}
    >
      <div className="FreeTrial__Glass__Head">
        <p>{t('authentication:trial_setup_eyebrow', { current: 2, total: 3 })}</p>
        <h1>{t('authentication:trial_brand_title')}</h1>
      </div>

      <div className="FreeTrial__LogoRow">
        <div className="FreeTrial__LogoRow__Preview">
          <BrandLogo
            variant="default"
            iconUrl={logoSources.iconUrl}
            logoUrl={logoSources.logoUrl}
            name={values.name}
          />
        </div>
        <div className="FreeTrial__LogoRow__Text">
          <strong>{t('authentication:trial_scan_logo')}</strong>
        </div>
        <label className="FreeTrial__Replace">
          <input
            type="file"
            accept="image/*"
            onChange={(event) => {
              const input = event.currentTarget;
              const file = input.files?.[0];

              input.value = '';
              if (!file) return;

              void (async () => {
                if (!isTrialLogoSourceWithinLimit(file)) {
                  setLogoError(true);
                  return;
                }

                const prepared = await prepareTrialLogo(file);

                if (!prepared) {
                  setLogoError(true);
                  return;
                }

                setLogoError(false);
                if (values.logoUrl?.startsWith('blob:')) URL.revokeObjectURL(values.logoUrl);
                onChange({ ...values, logoUrl: URL.createObjectURL(prepared), logoFile: prepared });
              })();
            }}
          />
          {t('authentication:trial_brand_replace')}
        </label>
      </div>
      {logoError && (
        <div className="FreeTrial__Hint">
          {t('common:form_input_alert_file_max_file_size', { maxFileSize: 8 })}
        </div>
      )}

      <div className="FreeTrial__FieldGrid">
        <div>
          <HexColorField
            id="trial-brand-main"
            label={t('authentication:trial_brand_main')}
            hex={mainHex}
            fallback={values.colors.main}
            onHexChange={setMainHex}
            onCommit={commitMain}
          />
          {!mainValid && <div className="FreeTrial__Hint">{t('authentication:trial_brand_hex_invalid')}</div>}
        </div>
        <div>
          <HexColorField
            id="trial-brand-second"
            label={t('authentication:trial_brand_second')}
            hex={secondHex}
            fallback={values.colors.button}
            onHexChange={setSecondHex}
            onCommit={commitSecond}
          />
          {!secondValid && <div className="FreeTrial__Hint">{t('authentication:trial_brand_hex_invalid')}</div>}
        </div>
      </div>

      <div className="FreeTrial__Glass__Actions">
        <button type="button" className="FreeTrial__Back" onClick={onBack}>
          <TrialBackArrow />
          <span>{t('authentication:trial_setup_back')}</span>
        </button>
        <Button type="primary" buttonType="submit" disabled={!mainValid || !secondValid}>
          <span className="FreeTrial__Cta">
            <Trans i18nKey="authentication:trial_preview_continue" />
            <TrialArrow />
          </span>
        </Button>
      </div>
    </form>
  );
});
